import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { useWriterComponentState } from "hooks";

import { AddRoutePointETAModalContent } from "./AddRoutePointETAModalContent";
import { ClientAppEvent } from "../../constants";

interface Props {
  onModalClose: VoidFunction;
  modalOpen: boolean;
  shipment: CondensedShipment;
  onETACreatedSuccess?: VoidFunction | null;
}

export function AddRoutePointETAModal(props: Props) {
  const {
    shipment,
    modalOpen,
    onModalClose,
    onETACreatedSuccess = null,
  } = props;

  const posthog = usePostHog();
  const [notes, setNotes] = useState<string>("");
  const [deliveryTimestamp, setDeliveryTimestamp] = useState<string>(
    DateTime.now().toFormat("yyyy-MM-dd'T'T"),
  );
  const [routePointId, setRoutePointId] = useState<number | undefined>(
    undefined,
  );

  const { loading, write, error } = useWriterComponentState({
    errorMessage:
      "Failed to create ETA. Please contact us if the issue persists.",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipment.info.id}/etas`,
      body: {
        notes,
        eta: DateTime.fromISO(deliveryTimestamp).toUTC().toISO(),
        point_id: routePointId,
      },
    },
    errorMessageKey: "message",
  });

  const clearState = () => {
    setNotes("");
    setDeliveryTimestamp(DateTime.now().toFormat("yyyy-MM-dd'T'T"));
    setRoutePointId(undefined);
  };

  const submit = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.ROUTE_POINT_ETA_ADDED, {
        route_point_index: routePointId,
        has_notes: notes.length > 0,
      });
      if (onETACreatedSuccess) {
        onETACreatedSuccess();
      }
      clearState();
      onModalClose();
    });
  };

  return (
    <Dialog fullWidth open={modalOpen} onClose={onModalClose}>
      <DialogTitle component="div">
        <Typography variant="h4">Add ETA</Typography>
      </DialogTitle>
      <DialogContent>
        <AddRoutePointETAModalContent
          notes={notes}
          onNotesUpdate={(newValue: string) => setNotes(newValue)}
          loading={loading}
          error={error}
          etaTimestamp={deliveryTimestamp}
          onEtaTimestampUpdate={(newValue: string) =>
            setDeliveryTimestamp(newValue)
          }
          shipment={shipment}
          routePointId={routePointId}
          setRoutePointId={setRoutePointId}
        />
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button autoFocus variant="outlined" onClick={onModalClose}>
            Go Back
          </Button>
          <Button onClick={submit} variant="contained">
            {!error ? "Add ETA" : "retry"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
